import { useId } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, ExternalLink, Loader2, Lock } from 'lucide-react'
import { getErrorMessage } from '@/api/errors'
import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/lib/utils'
import type { VariableGroupTriggerOptions, VariableKey } from '@/types'
import { FIELD_HINT_CLASS, FIELD_LABEL_CLASS } from './dynamicFieldUtils'
import { isVariableGroupAccessDenied } from './variableGroupOptions'

interface VariableGroupsFieldProps {
  projectId: string
  options: VariableGroupTriggerOptions | undefined
  isLoading: boolean
  error?: unknown
  selectedIds: string[]
  onSelectedIdsChange: (ids: string[]) => void
  requiredBy?: 'workflow' | 'chain'
}

function environmentVariablesPath(projectId: string) {
  return `/projects/${projectId}/testing-integration/environment-variables`
}

function KeyList({ keys }: { keys: VariableKey[] }) {
  if (keys.length === 0) return null

  return (
    <ul className="flex flex-wrap gap-x-3 gap-y-1" aria-label="Variables">
      {keys.map(({ key, isSecret }) => (
        <li key={key} className="flex items-center gap-1 font-mono text-[11px] text-muted-foreground">
          {key}
          {isSecret && <Lock className="h-3 w-3" role="img" aria-label="Secret" />}
        </li>
      ))}
    </ul>
  )
}

export function VariableGroupsField({
  projectId,
  options,
  isLoading,
  error,
  selectedIds,
  onSelectedIdsChange,
  requiredBy = 'workflow',
}: VariableGroupsFieldProps) {
  const headingId = useId()
  const required = options?.required ?? []
  const optional = options?.optional ?? []
  const pagePath = environmentVariablesPath(projectId)

  const toggle = (groupId: string, checked: boolean) =>
    onSelectedIdsChange(checked ? [...selectedIds, groupId] : selectedIds.filter(id => id !== groupId))

  return (
    <section role="group" aria-labelledby={headingId} className="flex flex-col gap-3" data-testid="variable-groups-field">
      <span id={headingId} className={cn(FIELD_LABEL_CLASS, 'text-sm')}>Variable Groups</span>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground" role="status">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading variable groups...
        </div>
      ) : isVariableGroupAccessDenied(error) ? (
        <p className={FIELD_HINT_CLASS}>
          You don't have permission to view variable groups in this project. Any groups this {requiredBy} requires are still checked when you start it.
        </p>
      ) : error ? (
        <p role="alert" className="flex items-start gap-1 text-xs text-destructive">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          {getErrorMessage(error) ?? 'The variable groups could not be loaded. Try again later.'}
        </p>
      ) : required.length === 0 && optional.length === 0 ? (
        <p className={FIELD_HINT_CLASS}>
          This project has no variable groups yet. Create them on the{' '}
          <Link to={pagePath} target="_blank" rel="noopener noreferrer" className="font-bold text-primary underline">
            Environment Variables page
          </Link>
          .
        </p>
      ) : (
        <>
          {required.length > 0 && (
            <div className="flex flex-col gap-1">
              <span className={FIELD_LABEL_CLASS}>Required by this {requiredBy}</span>
              <p className={FIELD_HINT_CLASS}>Defined in the workflow's YAML. Always included and can't be removed here.</p>
              <ul className="divide-y rounded-lg border bg-muted/40">
                {required.map(group => (
                  <li key={group.name} className="flex flex-col gap-1 px-3 py-2" aria-label={`${group.name} (required)`}>
                    <span className="flex items-center gap-2 text-sm font-bold">
                      <Lock className="h-4 w-4" aria-hidden />
                      {group.name}
                    </span>
                    {group.groupId === null ? (
                      <p role="alert" className="flex items-start gap-1 text-xs text-destructive">
                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                        <span>
                          This {requiredBy} requires the '{group.name}' group. Create it on the{' '}
                          <Link to={pagePath} target="_blank" rel="noopener noreferrer" className="font-bold underline">
                            Environment Variables page
                          </Link>{' '}
                          to continue.
                        </span>
                      </p>
                    ) : (
                      <KeyList keys={group.keys} />
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {optional.length > 0 && (
            <div className="flex flex-col gap-1">
              <span className={FIELD_LABEL_CLASS}>Additional groups (optional)</span>
              <p className={FIELD_HINT_CLASS}>All variables of a selected group are added to the run.</p>
              <ul className="divide-y rounded-lg border">
                {optional.map(group => {
                  const checkboxId = `${headingId}-${group.groupId}`
                  return (
                    <li key={group.groupId} className="flex items-start gap-3 px-3 py-2">
                      <Checkbox
                        id={checkboxId}
                        className="mt-0.5"
                        checked={selectedIds.includes(group.groupId)}
                        onCheckedChange={checked => toggle(group.groupId, checked === true)}
                      />
                      <div className="flex flex-col gap-1">
                        <label htmlFor={checkboxId} className="cursor-pointer text-sm font-bold">{group.name}</label>
                        <KeyList keys={group.keys} />
                      </div>
                    </li>
                  )
                })}
              </ul>
            </div>
          )}
        </>
      )}

      <Link
        to={pagePath}
        target="_blank"
        rel="noopener noreferrer"
        className="flex w-fit items-center gap-2 rounded-lg bg-muted px-3 py-1.5 text-sm font-bold text-foreground hover:bg-muted/80"
      >
        <ExternalLink className="h-4 w-4" aria-hidden />
        Manage variable groups
      </Link>
    </section>
  )
}
